import {
  buildTotals,
  cupsForDrink,
  type DrinkSize,
  deviceTimeZone,
  type FastfoodItem,
  type FoodUnit,
  localDateString,
  type MealSlot,
  type Per100g,
} from '@gramas/shared'
import {
  Button,
  EmptyState,
  List,
  ListRow,
  MacroPreview,
  SectionLabel,
  Segmented,
  Sheet,
  Stepper,
} from '@gramas/ui'
import { useQuery } from '@tanstack/react-query'
import { useMemo, useState, useSyncExternalStore } from 'react'
import { api } from '../api/client.js'
import { getMeta, setMeta } from '../db/dexie.js'
import { logAdHoc, slotFor } from '../db/repo.js'
import {
  orderComponents,
  orderKey,
  rememberOrder,
  subscribeUsual,
  usualOrder,
  usualSnapshot,
  usualsForChain,
} from '../db/usual.js'
import { PersonaSpinner } from '../glitime/PersonaSpinner.js'
import { logContext } from '../sass/context.js'
import { sassLine } from '../sass/engine.js'
import { maybeLogToast } from '../sass/toast.js'
import { useSassOnce } from '../sass/useSassOnce.js'
import { syncNow } from '../sync/engine.js'
import { ChainGrid } from './ChainGrid.js'
import { DrinkExtrasSection, type Extras, extraLines, extraSummary } from './sheets/DrinkExtras.js'
import { SlotPicker } from './sheets/SlotPicker.js'

interface Chain {
  id: number
  slug: string
  name: string
  /** The cups this chain pours, which are not the ones it publishes figures for. */
  drinkSizes: DrinkSize[] | null
}

type Component = FastfoodItem['components'][number]

/** What `/api/fastfood/components` returns: a part, with no item around it. */
interface LooseComponent {
  id: number
  name: string
  category: string | null
  unitWeightG: number | null
  unit: FoodUnit
  portionOnly: boolean
  kcal: number
  protein: number
  carbs: number
  fat: number
  sugars: number | null
  satFat: number | null
  fiber: number | null
  salt: number | null
}

function asSoloItem(component: LooseComponent, chainId: number, chainSlug: string): FastfoodItem {
  return {
    id: -component.id,
    chainId,
    chainSlug,
    name: component.name,
    category: component.category,
    groupKey: null,
    sizeLabel: null,
    portionWeightG: component.unitWeightG,
    unit: component.unit,
    isActive: true,
    components: [
      {
        componentId: component.id,
        name: component.name,
        category: component.category,
        weightG: component.unitWeightG,
        unitWeightG: component.unitWeightG,
        unit: component.unit,
        portionOnly: component.portionOnly,
        mandatory: true,
        isDefault: true,
        choiceGroup: null,
        position: 0,
        kcal: component.kcal,
        protein: component.protein,
        carbs: component.carbs,
        fat: component.fat,
        sugars: component.sugars,
        satFat: component.satFat,
        fiber: component.fiber,
        salt: component.salt,
      },
    ],
  }
}

/**
 * componentId → how many of that part are in the build. Absent means not chosen.
 *
 * A count, never a weight. Nobody orders 150 g of tortilla or 37 ml of sugar:
 * they order one tortilla and two sugars, and the grams behind those are the
 * chain's business. The one amount a person really does choose is the cup, and
 * that is asked separately, in the letters printed on it.
 */
type Choices = Map<number, number>

/**
 * `fixed` is what the item always contains, `swap` replaces one thing with
 * another, `extra` is added on top and can be taken as many times as you like.
 */
type GroupKind = 'fixed' | 'swap' | 'extra'

/**
 * Whether a part is in the build, said in a shape rather than a shade.
 *
 * Tinting the row was the whole signal before, and on a phone in daylight a
 * slightly lighter row is no signal at all. The shape carries the rule as well
 * as the state, the way it does everywhere else: a circle is one-of-these, a
 * square is any-of-these, and a part with no control at all is simply part of
 * the thing you ordered.
 */
function SelectMark({ kind, chosen }: { kind: GroupKind; chosen: boolean }) {
  if (kind === 'fixed') {
    return (
      <span
        aria-hidden="true"
        className="flex size-5 shrink-0 items-center justify-center text-mute"
      >
        <span className="size-1.5 rounded-full bg-mute" />
      </span>
    )
  }

  const round = kind === 'swap'
  return (
    <span
      aria-hidden="true"
      className={`flex size-5 shrink-0 items-center justify-center border-2 ${
        round ? 'rounded-full' : 'rounded-[5px]'
      } ${chosen ? 'border-accent bg-accent text-bg' : 'border-line text-transparent'}`}
    >
      <svg viewBox="0 0 14 14" className="size-3" fill="none" aria-hidden="true">
        <path
          d="M2.5 7.5l3 3 6-6.5"
          stroke="currentColor"
          strokeWidth="2.2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  )
}

/**
 * The cups a counter pours when the chain never says.
 *
 * Circle K publishes M/L/XL and most others publish nothing at all, which
 * leaves a per-100 ml latte with no volume to apply it to. These are the
 * standard Lithuanian coffee cups, and the sheet says so rather than passing
 * them off as the chain's own.
 */
const DEFAULT_CUPS: readonly DrinkSize[] = [
  { label: 'M', ml: 300 },
  { label: 'L', ml: 400 },
  { label: 'XL', ml: 450 },
]

/**
 * Counter food, assembled the way it is actually ordered.
 *
 * A hot dog is not one product: it is a bun, a sausage, and whichever sauces
 * you pointed at, and a latte is a cup plus a milk plus however many sugars.
 * So the menu item is the starting point and everything the chain publishes as
 * swappable is swappable here, with the macros re-totalling as you change it.
 */
export function FastFood() {
  const [chainSlug, setChainSlug] = useState<string | null>(null)
  const [openItemId, setOpenItemId] = useState<number | null>(null)
  // The order history lives outside React so the builder can read it while
  // rendering; this is how the list above it hears about a write.
  const orders = useSyncExternalStore(subscribeUsual, usualSnapshot, usualSnapshot)

  const chains = useQuery({
    queryKey: ['fastfood', 'chains'],
    queryFn: () => api.get<{ chains: Chain[] }>('/api/fastfood/chains').then((r) => r.chains),
  })

  const activeSlug = chainSlug ?? chains.data?.[0]?.slug ?? null

  const activeChain = (chains.data ?? []).find((c) => c.slug === activeSlug) ?? null

  const items = useQuery({
    queryKey: ['fastfood', 'items', activeSlug],
    queryFn: async () => {
      const { items } = await api.get<{ items: FastfoodItem[] }>(
        `/api/fastfood/items?chain=${activeSlug}`,
      )
      if (items.length > 0 || activeChain == null) return items
      // A chain whose scraper only ever produced loose parts still has to be
      // usable, so each part is presented as a menu item of one. Removing this
      // would empty the screen for every chain imported before menu items
      // existed.
      const { components } = await api.get<{ components: LooseComponent[] }>(
        `/api/fastfood/components?chainId=${activeChain.id}`,
      )
      return components.map((component) => asSoloItem(component, activeChain.id, activeChain.slug))
    },
    enabled: activeSlug != null,
  })

  const cupsFor = (item: FastfoodItem): readonly DrinkSize[] =>
    cupsForItem(item, activeChain?.drinkSizes ?? null)

  const byCategory = useMemo(() => groupByCategory(items.data ?? []), [items.data])
  const openItem = (items.data ?? []).find((i) => i.id === openItemId) ?? null

  /**
   * What you order here, at the top, without ever having saved anything.
   *
   * Only things ordered more than once — a single visit is not a habit, and a
   * list that fills with everything ever tried is a second copy of the menu.
   * `orderedAt` changes whenever a build is logged, which is what re-reads it.
   */
  const usuals = useMemo(() => {
    if (activeSlug == null) return []
    const all = items.data ?? []
    return usualsForChain(activeSlug, orders)
      .flatMap((entry) => {
        const item = all.find((i) => i.id === entry.itemId)
        return item ? [{ item, count: entry.count }] : []
      })
      .slice(0, 4)
  }, [activeSlug, items.data, orders])

  const chainsEmpty = chains.isError || (chains.data ?? []).length === 0
  const chainsEmptyTitle = useSassOnce(
    chainsEmpty ? 'chains' : null,
    () => sassLine('empty_list', { surface: 'fastfood' }) ?? 'Tinklų duomenų nėra',
  )
  const itemsEmpty = !items.isLoading && (items.data ?? []).length === 0
  const itemsEmptyTitle = useSassOnce(
    itemsEmpty ? 'items' : null,
    () => sassLine('empty_list', { surface: 'fastfood' }) ?? 'Meniu dar nesurinktas',
  )

  if (chains.isLoading) {
    return (
      <div className="flex justify-center py-10">
        <PersonaSpinner surface="foods" />
      </div>
    )
  }

  if (chainsEmpty) {
    return (
      <EmptyState
        title={chainsEmptyTitle ?? ''}
        hint="Greito maisto meniu surenkamas serveryje. Patikrinkite ryšį."
      />
    )
  }

  return (
    <div className="flex h-full flex-col">
      {/* A dropdown hid seven of the eight chains behind a tap and told you
          nothing about any of them. The shelf is the same idea as the produce
          tiles: everything visible at once, chosen by pointing at it. */}
      <div className="shrink-0 border-b border-line pt-2">
        <ChainGrid chains={chains.data ?? []} value={activeSlug} onChange={setChainSlug} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
        {items.isLoading && (
          <div className="flex justify-center py-8">
            <PersonaSpinner surface="foods" />
          </div>
        )}

        {itemsEmpty && (
          <EmptyState
            title={itemsEmptyTitle ?? ''}
            hint="Šio tinklo produktai bus atnaujinti per mėnesinį duomenų surinkimą."
          />
        )}

        {usuals.length > 0 && (
          <section>
            <SectionLabel>Jūsų įprasti</SectionLabel>
            <List className="border-y border-line bg-surface">
              {usuals.map(({ item, count }, i) => (
                <ListRow
                  key={`usual-${item.id}`}
                  last={i === usuals.length - 1}
                  title={item.name}
                  subtitle={`${count}× · ${describe(item, cupsFor(item))}`}
                  value={`${Math.round(itemKcal(item, cupsFor(item)))}`}
                  meta="kcal"
                  onActivate={() => setOpenItemId(item.id)}
                />
              ))}
            </List>
          </section>
        )}

        {byCategory.map(([category, list]) => (
          <section key={category}>
            <SectionLabel>{category}</SectionLabel>
            <List className="border-y border-line bg-surface">
              {list.map((item, i) => (
                <ListRow
                  key={item.id}
                  last={i === list.length - 1}
                  title={item.name}
                  subtitle={describe(item, cupsFor(item))}
                  value={`${Math.round(itemKcal(item, cupsFor(item)))}`}
                  meta="kcal"
                  onActivate={() => setOpenItemId(item.id)}
                />
              ))}
            </List>
          </section>
        ))}
        <div className="h-8" />
      </div>

      <BuilderSheet
        item={openItem}
        siblings={(items.data ?? []).filter(
          (i) => openItem?.groupKey != null && i.groupKey === openItem.groupKey,
        )}
        chainName={activeChain?.name ?? ''}
        chainSlug={activeChain?.slug ?? ''}
        cups={openItem ? cupsFor(openItem) : []}
        chainPublishesCups={activeChain?.drinkSizes != null}
        onOpenChange={(open) => !open && setOpenItemId(null)}
        onSwitchSize={setOpenItemId}
        onLogged={() => setOpenItemId(null)}
      />
    </div>
  )
}

function groupByCategory(items: readonly FastfoodItem[]): Array<[string, FastfoodItem[]]> {
  const map = new Map<string, FastfoodItem[]>()
  for (const item of items) {
    // Size variants collapse to one row: showing "Latte 0,3 l" and "Latte 0,4 l"
    // side by side turns a 40-item menu into an 80-item one for no information.
    const key = item.category ?? 'Kita'
    const list = map.get(key) ?? []
    if (item.groupKey != null && list.some((i) => i.groupKey === item.groupKey)) continue
    list.push(item)
    map.set(key, list)
  }
  return [...map.entries()]
}

/** Anything poured gets a cup instead of a portion; everything else is eaten. */
function isPoured(item: FastfoodItem): boolean {
  return item.unit === 'ml'
}

function describe(item: FastfoodItem, cups: readonly DrinkSize[]): string {
  const parts: string[] = []
  const cup = cups[0]
  if (cup != null) parts.push(`${cup.label} ${cup.ml} ml`)
  else parts.push('1 porcija')
  const swappable = item.components.filter((c) => !c.mandatory).length
  if (swappable > 0) parts.push(`${swappable} pasirinkimai`)
  return parts.join(' · ')
}

/** Kcal of the item as the counter would hand it over: one portion, first cup. */
function itemKcal(item: FastfoodItem, cups: readonly DrinkSize[]): number {
  return asOrdered(item, cups).totals.kcal
}

/**
 * The item as it comes, with nothing changed.
 *
 * Exported so search can offer a counter item without dragging the whole
 * builder onto the screen: the overwhelmingly common order is the default one,
 * and anyone who wants to swap the milk still has the chain's own tab.
 */
export function asOrdered(
  item: FastfoodItem,
  cups: readonly DrinkSize[],
): ReturnType<typeof buildTotals> {
  return totalsFor(item, defaultChoices(item), new Map(), {
    portions: 1,
    cupMl: cups[0]?.ml ?? null,
  })
}

/**
 * The cups to offer for one item.
 *
 * Two questions, in order. Is it poured at all — a burger has portions, not
 * millilitres. And is it poured as a shot: Vero Cafe's 35 ml "Greitukas" is a
 * real size for an espresso and nonsense for a latte, so the espresso family
 * gets the shot glasses and everything else gets the cups.
 */
export function cupsForItem(
  item: FastfoodItem,
  chainCups: DrinkSize[] | null,
): readonly DrinkSize[] {
  if (!isPoured(item)) return []
  return cupsForDrink(item.name, chainCups ?? DEFAULT_CUPS)
}

function defaultChoices(item: FastfoodItem): Choices {
  const choices: Choices = new Map()
  const groups = groupComponents(item)
  for (const group of groups) {
    // An addition starts off. Circle K marks the first sugar of "Kavos priedai"
    // as the default, which put sugar in every coffee on the board and made
    // "I did not have sugar" a thing you had to notice and undo.
    if (group.kind === 'extra') continue
    for (const component of group.components) {
      if (component.mandatory || component.isDefault) choices.set(component.componentId, 1)
    }
  }
  // An item that is nothing but choices — the ice creams, where every scoop is
  // optional — would otherwise open at zero kcal with the log button dead and
  // no hint that a flavour is what is missing. The first one stands in until a
  // real one is picked.
  const first = groups[0]?.components[0]
  if (choices.size === 0 && first != null) choices.set(first.componentId, 1)
  return choices
}

/** The grams the chain publishes for one of this part. */
function unitGrams(component: Component): number {
  return component.weightG ?? component.unitWeightG ?? 0
}

/** "1 porcija", "0,5 porcijos" — Lithuanian decimals use a comma. */
function portionLabel(portions: number): string {
  if (Math.abs(portions - 1) < 0.01) return '1 porcija'
  const rounded = Math.round(portions * 10) / 10
  return `${String(rounded).replace('.', ',')} porcijos`
}

interface Amounts {
  /** How many of the whole thing, for something eaten. */
  portions: number
  /** The cup it is poured into, for something drunk. */
  cupMl: number | null
}

/**
 * The grams behind the build, part by part.
 *
 * Two multipliers, and which one applies is the whole point. What is poured
 * scales with the cup — a large latte is the same drink with more of it — while
 * what is spooned in does not: two sugars are two sugars whether the cup is 300
 * or 450. Scaling the sugar with the cup was the arithmetic that made an XL
 * coffee quietly claim three sugars.
 */
function gramsByComponent(
  item: FastfoodItem,
  choices: Choices,
  amounts: Amounts,
): Map<number, number> {
  const groups = groupComponents(item)
  const structural = groups.filter((g) => g.kind !== 'extra').flatMap((g) => g.components)
  const additions = groups.filter((g) => g.kind === 'extra').flatMap((g) => g.components)

  /**
   * How much bigger the cup is than the drink the chain published — measured
   * against the *default* build, not the current one.
   *
   * Against the current one it moved every time anything was ticked: choosing
   * oat milk changed the divisor, so the coffee, the syrup and the milk all
   * jumped at once and the whole panel looked like it was reshuffling itself.
   * The default build is the drink the chain's own figures describe, and it is
   * the same drink whichever milk goes in it.
   */
  const defaults = defaultChoices(item)
  const publishedG = structural.reduce(
    (sum, c) => sum + unitGrams(c) * (defaults.get(c.componentId) ?? 0),
    0,
  )
  const cupScale = amounts.cupMl != null && publishedG > 0 ? amounts.cupMl / publishedG : 1

  const grams = new Map<number, number>()
  for (const component of structural) {
    const count = choices.get(component.componentId) ?? 0
    if (count > 0)
      grams.set(component.componentId, unitGrams(component) * count * cupScale * amounts.portions)
  }
  for (const component of additions) {
    const count = choices.get(component.componentId) ?? 0
    if (count > 0) grams.set(component.componentId, unitGrams(component) * count * amounts.portions)
  }
  return grams
}

function totalsFor(
  item: FastfoodItem,
  choices: Choices,
  extras: Extras,
  amounts: Amounts,
): ReturnType<typeof buildTotals> {
  const grams = gramsByComponent(item, choices, amounts)
  return buildTotals([
    ...item.components.flatMap((component) => {
      const g = grams.get(component.componentId) ?? 0
      if (g <= 0) return []
      return [{ per100g: per100gOf(component), unitWeightG: g, quantity: 1 }]
    }),
    ...extraLines(extras, amounts.portions),
  ])
}

function per100gOf(component: Component): Per100g {
  return {
    kcal: component.kcal,
    protein: component.protein,
    carbs: component.carbs,
    fat: component.fat,
    sugars: component.sugars,
    satFat: component.satFat,
    fiber: component.fiber,
    salt: component.salt,
  }
}

/**
 * The cup someone last asked for, per chain.
 *
 * Nobody orders a different size every visit — a person who takes the largest
 * cappuccino takes it again tomorrow — so remembering it makes the common order
 * zero taps without asking anyone to configure a favourite. Kept in a module
 * variable as well as in storage because the sheet decides its default while
 * rendering, and reading Dexie there would mean a frame of the wrong size.
 */
const CUP_MEMORY_KEY = 'fastfood.cup'
let cupMemory: Record<string, number> = {}
void getMeta<Record<string, number>>(CUP_MEMORY_KEY, {}).then((stored) => {
  cupMemory = stored
})

function rememberCup(chainSlug: string, ml: number): void {
  if (chainSlug === '') return
  cupMemory = { ...cupMemory, [chainSlug]: ml }
  void setMeta(CUP_MEMORY_KEY, cupMemory)
}

/** The remembered cup, but only while the chain still pours it. */
function defaultCup(chainSlug: string, cups: readonly DrinkSize[]): number | null {
  const remembered = cupMemory[chainSlug]
  if (remembered != null && cups.some((cup) => cup.ml === remembered)) return remembered
  return cups[0]?.ml ?? null
}

function BuilderSheet({
  item,
  siblings,
  chainName,
  chainSlug,
  cups,
  chainPublishesCups,
  onOpenChange,
  onSwitchSize,
  onLogged,
}: {
  item: FastfoodItem | null
  siblings: readonly FastfoodItem[]
  chainName: string
  chainSlug: string
  cups: readonly DrinkSize[]
  chainPublishesCups: boolean
  onOpenChange: (open: boolean) => void
  onSwitchSize: (id: number) => void
  onLogged: () => void
}) {
  const [choices, setChoices] = useState<Choices>(new Map())
  const [extras, setExtras] = useState<Extras>(new Map())
  const [portions, setPortions] = useState(1)
  const [cupMl, setCupMl] = useState<number | null>(null)
  const [slot, setSlot] = useState<MealSlot>(slotFor(null, 'g'))
  const [saving, setSaving] = useState(false)
  // Re-deriving defaults on every item change inside an effect would fight the
  // user's taps; keying off the id and resetting on change keeps one source.
  const [initialisedFor, setInitialisedFor] = useState<{
    id: number
    groupKey: FastfoodItem['groupKey']
  } | null>(null)

  if (item != null && initialisedFor?.id !== item.id) {
    // Only a size switch is the same order in a different cup. Anything else is
    // a different thing off the menu, and carrying a build into it leaked the
    // last drink's syrup into an unrelated one — the component ids are the
    // chain's, not the item's, so they matched.
    const sameFamily =
      initialisedFor != null && item.groupKey != null && initialisedFor.groupKey === item.groupKey
    setInitialisedFor({ id: item.id, groupKey: item.groupKey })
    setSlot(slotFor(item.category, item.unit, item.name))

    /**
     * Open on the version you order most here.
     *
     * Only when it is the same size variant: switching from a small to a large
     * is the user asking for a different thing, and restoring the small one's
     * build over the top of that would be the feature fighting the tap that
     * invoked it. Everything else — the milk, the sauces, the cup — carries.
     */
    const usual = usualOrder(orderKey(chainSlug, item.groupKey, item.id))
    const restoring = usual != null && usual.itemId === item.id

    setPortions(restoring ? usual.portions : 1)
    setCupMl(restoring ? usual.cupMl : defaultCup(chainSlug, cups))
    setExtras(restoring ? new Map(usual.extras) : new Map())

    setChoices((previous) => {
      if (restoring) {
        const known = new Set(item.components.map((c) => c.componentId))
        // A component the chain has since retired is simply dropped; the rest
        // of a remembered order is still the order.
        return new Map(orderComponents(usual).filter(([id]) => known.has(id)))
      }
      // Carry the extras across a size switch: someone who added bacon to a
      // small burger still wants bacon on the large one. Only the extras: a
      // swap group holds one of its members by definition, and laying the
      // previous choice over the new item's default left both the cow milk and
      // the oat milk in the cup, each counted in full.
      const next = defaultChoices(item)
      if (!sameFamily) return next
      const addable = new Set(
        groupComponents(item)
          .filter((group) => group.kind === 'extra')
          .flatMap((group) => group.components.map((c) => c.componentId)),
      )
      for (const component of item.components) {
        const carried = previous.get(component.componentId)
        if (carried != null && addable.has(component.componentId)) {
          next.set(component.componentId, carried)
        }
      }
      return next
    })
  }

  const groups = useMemo(() => (item ? groupComponents(item) : []), [item])
  const sizes = [...siblings].sort((a, b) => (a.sizeLabel ?? '').localeCompare(b.sizeLabel ?? ''))
  const poured = item != null && isPoured(item)
  // The chain's own additions win: a Circle K sugar stick is a stated 3 g, and
  // the generic teaspoon below it is a guess. Both at once is two sugar rows.
  const showExtras = poured && !groups.some((g) => g.kind === 'extra')
  const amounts: Amounts = { portions, cupMl: poured ? cupMl : null }
  const totals = item ? totalsFor(item, choices, extras, amounts) : null
  // Not memoized: an item has at most a couple of dozen parts, and the cost of
  // getting the dependency list wrong here is a row showing last render's grams.
  const shares = item ? gramsByComponent(item, choices, amounts) : new Map<number, number>()

  const commit = async () => {
    if (!item || !totals || saving) return
    setSaving(true)
    try {
      const cup = cups.find((c) => c.ml === cupMl)
      const loggedName = nameFor(item, chainName, cup, extras)
      await logAdHoc(loggedName, totals.totals, {
        slot,
        quantityG: Math.max(1, totals.weightG),
        // Fast-food is counted, not weighed, so the diary records the count and
        // says "1 porcija" rather than printing grams nobody chose.
        servings: portions,
        logDate: localDateString(new Date(), deviceTimeZone()),
      })
      // Nothing is named and nothing is managed: ordering the same thing twice
      // is what makes it the thing this item opens on next time.
      const chosen = [...choices.entries()].filter(([, count]) => count > 0)
      await rememberOrder(orderKey(chainSlug, item.groupKey, item.id), {
        itemId: item.id,
        components: chosen,
        componentIds: chosen.map(([id]) => id),
        extras: [...extras.entries()],
        cupMl: poured ? cupMl : null,
        portions,
      })
      void syncNow()
      onLogged()
      maybeLogToast(
        await logContext(
          localDateString(new Date(), deviceTimeZone()),
          slot,
          Math.round(totals.totals.kcal),
          loggedName,
        ),
      )
    } finally {
      setSaving(false)
    }
  }

  return (
    <Sheet
      open={item != null}
      onOpenChange={onOpenChange}
      title={item?.name ?? ''}
      description={
        item == null ? undefined : poured ? cupLabel(cups, cupMl) : portionLabel(portions)
      }
      footer={
        <Button
          variant="primary"
          block
          size="lg"
          disabled={saving || totals == null || totals.totals.kcal <= 0}
          onClick={() => void commit()}
        >
          {saving ? 'Įrašoma…' : `Įrašyti ${Math.round(totals?.totals.kcal ?? 0)} kcal`}
        </Button>
      }
    >
      {item && totals && (
        <div className="flex flex-col gap-1 pb-4">
          {sizes.length > 1 && (
            <div className="px-4 pt-4">
              <Segmented
                label="Variantas"
                value={String(item.id)}
                onValueChange={(value) => onSwitchSize(Number(value))}
                className="w-full"
                options={sizes.map((s) => ({
                  value: String(s.id),
                  label: s.sizeLabel ?? s.name,
                }))}
              />
            </div>
          )}

          {poured && cups.length > 0 && (
            <div className="flex flex-col gap-1.5 px-4 pt-4">
              <Segmented
                label="Puodelis"
                value={String(cupMl ?? cups[0]?.ml ?? '')}
                onValueChange={(value) => {
                  setCupMl(Number(value))
                  rememberCup(chainSlug, Number(value))
                }}
                className="w-full"
                options={cups.map((cup) => ({ value: String(cup.ml), label: cup.label }))}
              />
              <p className="text-xs text-mute">
                {chainPublishesCups
                  ? 'Tinklas skelbia vieną standartinį puodelį — pasirinkus kitą dydį, gėrimo dalys perskaičiuojamos proporcingai.'
                  : 'Tinklas puodelio tūrio neskelbia, todėl siūlomi įprasti dydžiai.'}
              </p>
            </div>
          )}

          {!poured && (
            <div className="px-4 pt-4">
              <Segmented
                label="Kiek"
                value={String(portions)}
                onValueChange={(value) => setPortions(Number(value))}
                className="w-full"
                options={[0.5, 1, 2, 3].map((p) => ({
                  value: String(p),
                  label: p === 1 ? '1 porcija' : String(p).replace('.', ','),
                }))}
              />
            </div>
          )}

          <MacroPreview
            className="mx-4 mt-4"
            kcal={totals.totals.kcal}
            proteinG={totals.totals.protein}
            carbsG={totals.totals.carbs}
            fatG={totals.totals.fat}
          />

          <div className="mx-4 mt-4">
            <SlotPicker value={slot} onChange={setSlot} />
          </div>

          {groups.map((group) => (
            <section key={group.key}>
              <SectionLabel>{group.title}</SectionLabel>
              <List className="border-y border-line bg-surface">
                {group.components.map((component, i) => {
                  const count = choices.get(component.componentId) ?? 0
                  // What this part actually contributes to the build in front
                  // of you — the milk in an XL is more milk than in an M.
                  const grams = shares.get(component.componentId) ?? unitGrams(component)
                  const kcal = Math.round((component.kcal * grams) / 100)
                  return (
                    <ListRow
                      key={component.componentId}
                      last={i === group.components.length - 1}
                      title={component.name}
                      subtitle={
                        group.kind === 'fixed'
                          ? 'privaloma'
                          : count > 0
                            ? `įdėta${count > 1 ? ` ×${count}` : ''}`
                            : undefined
                      }
                      value={`${kcal}`}
                      meta="kcal"
                      leading={<SelectMark kind={group.kind} chosen={count > 0} />}
                      className={count > 0 ? 'bg-raised' : undefined}
                      accessory={
                        group.kind === 'extra' ? (
                          <Stepper
                            value={count}
                            onChange={(next) =>
                              setChoices((previous) =>
                                setCount(previous, component.componentId, next),
                              )
                            }
                          />
                        ) : undefined
                      }
                      onActivate={
                        group.kind === 'fixed'
                          ? undefined
                          : group.kind === 'swap'
                            ? () =>
                                setChoices((previous) =>
                                  pickOne(previous, group.components, component),
                                )
                            : undefined
                      }
                    />
                  )
                })}
              </List>
            </section>
          ))}

          {showExtras && (
            <DrinkExtrasSection
              extras={extras}
              onChange={setExtras}
              note="Įprastos vertės — tinklas priedų neskelbia."
            />
          )}
        </div>
      )}
    </Sheet>
  )
}

function cupLabel(cups: readonly DrinkSize[], cupMl: number | null): string {
  const cup = cups.find((c) => c.ml === cupMl) ?? cups[0]
  return cup ? `${cup.label} · ${cup.ml} ml` : ''
}

interface ComponentGroup {
  key: string
  title: string
  /**
   * `fixed` is what the item always contains, `swap` replaces one thing with
   * another, `extra` is added on top and can be taken as many times as you like.
   */
  kind: GroupKind
  components: Component[]
}

/**
 * The groups where the counter swaps one thing for another rather than adding.
 *
 * Circle K's API does not say which of its groups are exclusive, and the answer
 * is not in the data — it is what happens at the counter. A latte has one milk
 * in it and a hot dog has one sausage, so choosing a second replaces the first;
 * sauces, vegetables and everything on the coffee tray stack instead. Naming the
 * swaps and letting the rest stack is the way round that fails safe: a wrong
 * guess here lets you order two of something, not silently drop one.
 */
const SWAP_GROUP = /(pienas|augalinis|dešrel|desrel|duonos gamin|bandel)/i

/**
 * Split an item's parts into what the counter would actually ask you.
 *
 * The mandatory parts are listed so the numbers are explicable, but they cannot
 * be removed and they carry no amount: nobody is asked how many grams of
 * tortilla they would like.
 */
function groupComponents(item: FastfoodItem): ComponentGroup[] {
  const fixed = item.components.filter((c) => c.mandatory).sort(byPosition)
  const groups = new Map<string, Component[]>()
  const loose: Component[] = []

  for (const component of item.components) {
    if (component.mandatory) continue
    if (component.choiceGroup != null) {
      groups.set(component.choiceGroup, [...(groups.get(component.choiceGroup) ?? []), component])
    } else {
      loose.push(component)
    }
  }

  const out: ComponentGroup[] = []
  if (fixed.length > 0) {
    out.push({ key: 'fixed', title: 'Sudėtis', kind: 'fixed', components: fixed })
  }
  for (const [title, components] of groups) {
    out.push({
      key: `g:${title}`,
      title,
      kind: SWAP_GROUP.test(title) ? 'swap' : 'extra',
      components: components.sort(byPosition),
    })
  }
  if (loose.length > 0) {
    out.push({
      key: 'extras',
      title: 'Papildomai',
      kind: 'extra',
      components: loose.sort(byPosition),
    })
  }
  return out
}

function byPosition(a: Component, b: Component): number {
  return a.position - b.position || a.name.localeCompare(b.name)
}

function setCount(previous: Choices, componentId: number, next: number): Choices {
  const out = new Map(previous)
  if (next <= 0) out.delete(componentId)
  else out.set(componentId, Math.min(10, next))
  return out
}

/** Choosing a milk unchooses the other milks; choosing it again takes it out. */
function pickOne(previous: Choices, siblings: readonly Component[], chosen: Component): Choices {
  const next = new Map(previous)
  const wasChosen = (previous.get(chosen.componentId) ?? 0) > 0
  for (const sibling of siblings) next.delete(sibling.componentId)
  if (!wasChosen) next.set(chosen.componentId, 1)
  return next
}

function nameFor(
  item: FastfoodItem,
  chainName: string,
  cup: DrinkSize | undefined,
  extras: Extras,
): string {
  const size = item.sizeLabel != null ? ` ${item.sizeLabel}` : ''
  // The cup goes in the diary entry's name: three months later "Circle K Kava
  // su pienu" says nothing about whether it was the 300 or the 450.
  const poured = cup ? ` ${cup.label} ${cup.ml} ml` : ''
  const added = extraSummary(extras)
  return `${chainName} ${item.name}${size}${poured}${added}`.trim()
}
